import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearFilterOptionsCache,
  getFilterMatchCount,
  getFilterOptions,
} from '../answersService';
import {
  Building,
  FileText,
  Folder,
  Globe,
  MapPin,
  Package,
  ShieldCheck,
} from 'lucide-react';
import {
  AnswersRagAccordionFilter,
  AnswersRagDateRangeFilter,
  AnswersRagObjectionsFilter,
} from '../components/answersRagFilters';

const JURISDICTION_MAP = {
  Compact: 'Compact',
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR',
  California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID',
  Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
  Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
  Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV',
  Wisconsin: 'WI', Wyoming: 'WY', DC: 'DC',
};

const FILTER_DIMENSION_KEYS = [
  'filing_types', 'jurisdictions', 'states', 'submitted_dates', 'disposition_dates',
  'companies', 'sub_toi', 'toi', 'state_toi', 'state_sub_toi', 'statuses', 'product_names', 'has_objections',
];

const defaultParams = {
  filingTypes: [],
  jurisdictions: [],
  states: [],
  companies: [],
  toi: [],
  subToi: [],
  stateToi: [],
  stateSubToi: [],
  statuses: [],
  productNames: [],
  hasObjections: '',
};

const labelForJurisdiction = (abbr) => {
  const label = Object.keys(JURISDICTION_MAP).find((key) => JURISDICTION_MAP[key] === abbr);
  return label || abbr;
};

const labelForStateCode = (code) => labelForJurisdiction(code);

const mapJurisdictionOptions = (values = []) => values.map((value) => ({
  label: labelForJurisdiction(value) !== value ? labelForJurisdiction(value) : value,
  value,
}));

const mapStateOptions = (values = []) => values.map((code) => ({
  label: labelForStateCode(code),
  value: code,
}));

/** Landing / footer shortcuts → accordion section + scroll target. */
const FILTER_SECTION_BY_KEY = {
  type: 'filingTypes',
  state: 'states',
  filing: 'filingTypes',
  jurisdiction: 'jurisdictions',
  taxonomy: 'toi',
  company: 'companies',
};

const cloneParams = (p) => ({
  filingTypes: [...p.filingTypes],
  jurisdictions: [...p.jurisdictions],
  states: [...p.states],
  companies: [...p.companies],
  toi: [...p.toi],
  subToi: [...p.subToi],
  stateToi: [...p.stateToi],
  stateSubToi: [...p.stateSubToi],
  statuses: [...p.statuses],
  productNames: [...p.productNames],
  hasObjections: p.hasObjections,
});

const countActiveFilters = (
  params,
  dateStart,
  dateEnd,
  dispositionDateStart,
  dispositionDateEnd,
  isFilterEnabled,
) => {
  let n = 0;
  if (isFilterEnabled('filing_types') && params.filingTypes.length) n++;
  if (isFilterEnabled('jurisdictions') && params.jurisdictions.length) n++;
  if (isFilterEnabled('states') && params.states.length) n++;
  if (isFilterEnabled('companies') && params.companies.length) n++;
  if (isFilterEnabled('toi') && params.toi.length) n++;
  if (isFilterEnabled('sub_toi') && params.subToi.length) n++;
  if (isFilterEnabled('state_toi') && params.stateToi.length) n++;
  if (isFilterEnabled('state_sub_toi') && params.stateSubToi.length) n++;
  if (isFilterEnabled('statuses') && params.statuses.length) n++;
  if (isFilterEnabled('product_names') && params.productNames.length) n++;
  if (isFilterEnabled('submitted_dates') && dateStart && dateEnd) n++;
  if (isFilterEnabled('disposition_dates') && dispositionDateStart && dispositionDateEnd) n++;
  if (isFilterEnabled('has_objections') && (params.hasObjections === 'true' || params.hasObjections === 'false')) n++;
  return n;
};

const isDimensionEnabled = (ui, dim) => ui[dim] !== false;

const pruneParamsForEnabled = (params, ui) => ({
  filingTypes: isDimensionEnabled(ui, 'filing_types') ? params.filingTypes : [],
  jurisdictions: isDimensionEnabled(ui, 'jurisdictions') ? params.jurisdictions : [],
  states: isDimensionEnabled(ui, 'states') ? params.states : [],
  companies: isDimensionEnabled(ui, 'companies') ? params.companies : [],
  toi: isDimensionEnabled(ui, 'toi') ? params.toi : [],
  subToi: isDimensionEnabled(ui, 'sub_toi') ? params.subToi : [],
  stateToi: isDimensionEnabled(ui, 'state_toi') ? params.stateToi : [],
  stateSubToi: isDimensionEnabled(ui, 'state_sub_toi') ? params.stateSubToi : [],
  statuses: isDimensionEnabled(ui, 'statuses') ? params.statuses : [],
  productNames: isDimensionEnabled(ui, 'product_names') ? params.productNames : [],
  hasObjections: isDimensionEnabled(ui, 'has_objections') ? params.hasObjections : '',
});

const buildPayloadFrom = (
  params,
  dateStart,
  dateEnd,
  dispositionDateStart,
  dispositionDateEnd,
  isFilterEnabled,
) => ({
  jurisdictions: isFilterEnabled('jurisdictions') && params.jurisdictions.length ? [...params.jurisdictions] : undefined,
  states: isFilterEnabled('states') && params.states.length ? [...params.states] : undefined,
  companies: isFilterEnabled('companies') && params.companies.length ? [...params.companies] : undefined,
  filing_types: isFilterEnabled('filing_types') && params.filingTypes.length ? [...params.filingTypes] : undefined,
  toi: isFilterEnabled('toi') && params.toi.length ? [...params.toi] : undefined,
  sub_toi: isFilterEnabled('sub_toi') && params.subToi.length ? [...params.subToi] : undefined,
  state_toi: isFilterEnabled('state_toi') && params.stateToi.length ? [...params.stateToi] : undefined,
  state_sub_toi: isFilterEnabled('state_sub_toi') && params.stateSubToi.length ? [...params.stateSubToi] : undefined,
  statuses: isFilterEnabled('statuses') && params.statuses.length ? [...params.statuses] : undefined,
  product_names: isFilterEnabled('product_names') && params.productNames.length ? [...params.productNames] : undefined,
  date_start: isFilterEnabled('submitted_dates') && dateStart && dateEnd ? dateStart : undefined,
  date_end: isFilterEnabled('submitted_dates') && dateStart && dateEnd ? dateEnd : undefined,
  disposition_date_start: isFilterEnabled('disposition_dates') && dispositionDateStart && dispositionDateEnd ? dispositionDateStart : undefined,
  disposition_date_end: isFilterEnabled('disposition_dates') && dispositionDateStart && dispositionDateEnd ? dispositionDateEnd : undefined,
  has_objections: isFilterEnabled('has_objections')
    ? (params.hasObjections === 'true' ? true : params.hasObjections === 'false' ? false : undefined)
    : undefined,
});

const buildScopeChips = (
  params,
  dateStart,
  dateEnd,
  dispositionDateStart,
  dispositionDateEnd,
  isFilterEnabled,
) => {
  const chips = [];
  const pushCount = (label, count) => {
    if (count > 0) chips.push({ label, count });
  };

  if (isFilterEnabled('companies') && params.companies.length) pushCount('Company', params.companies.length);
  if (isFilterEnabled('jurisdictions') && params.jurisdictions.length) pushCount('Jurisdiction', params.jurisdictions.length);
  if (isFilterEnabled('states') && params.states.length) pushCount('State', params.states.length);
  if (isFilterEnabled('filing_types') && params.filingTypes.length) pushCount('Filing type', params.filingTypes.length);
  if (isFilterEnabled('product_names') && params.productNames.length) pushCount('Product', params.productNames.length);
  if (isFilterEnabled('toi') && params.toi.length) pushCount('TOI', params.toi.length);
  if (isFilterEnabled('sub_toi') && params.subToi.length) pushCount('Sub-TOI', params.subToi.length);
  if (isFilterEnabled('state_toi') && params.stateToi.length) pushCount('State TOI', params.stateToi.length);
  if (isFilterEnabled('state_sub_toi') && params.stateSubToi.length) pushCount('State Sub-TOI', params.stateSubToi.length);
  if (isFilterEnabled('statuses') && params.statuses.length) pushCount('Status', params.statuses.length);
  if (isFilterEnabled('submitted_dates') && dateStart && dateEnd) pushCount('Submitted', 1);
  if (isFilterEnabled('disposition_dates') && dispositionDateStart && dispositionDateEnd) pushCount('Disposition', 1);
  if (isFilterEnabled('has_objections') && params.hasObjections) pushCount('Objections', 1);

  return chips;
};

const FILTER_DOM_IDS = {
  filingTypes: 'answers-filter-filingTypes',
  statuses: 'answers-filter-statuses',
  hasObjections: 'answers-filter-hasObjections',
  jurisdictions: 'answers-filter-jurisdictions',
  states: 'answers-filter-states',
  submittedDates: 'answers-filter-submittedDates',
  dispositionDates: 'answers-filter-dispositionDates',
  companies: 'answers-filter-companies',
  productNames: 'answers-filter-productNames',
  toi: 'answers-filter-toi',
  subToi: 'answers-filter-subToi',
  stateToi: 'answers-filter-stateToi',
  stateSubToi: 'answers-filter-stateSubToi',
};

/** Drawer section key → enabled_filters dimension (server /query/filter-options). */
const FILTER_SECTION_TO_DIMENSION = {
  filingTypes: 'filing_types',
  statuses: 'statuses',
  hasObjections: 'has_objections',
  jurisdictions: 'jurisdictions',
  states: 'states',
  companies: 'companies',
  productNames: 'product_names',
  toi: 'toi',
  subToi: 'sub_toi',
  stateToi: 'state_toi',
  stateSubToi: 'state_sub_toi',
};

export function useAnswersRagFilters() {
  const [appliedParams, setAppliedParams] = useState(defaultParams);
  const [appliedDateStart, setAppliedDateStart] = useState('');
  const [appliedDateEnd, setAppliedDateEnd] = useState('');
  const [appliedDispositionDateStart, setAppliedDispositionDateStart] = useState('');
  const [appliedDispositionDateEnd, setAppliedDispositionDateEnd] = useState('');
  const [draftParams, setDraftParams] = useState(defaultParams);
  const [draftDateStart, setDraftDateStart] = useState('');
  const [draftDateEnd, setDraftDateEnd] = useState('');
  const [draftDispositionDateStart, setDraftDispositionDateStart] = useState('');
  const [draftDispositionDateEnd, setDraftDispositionDateEnd] = useState('');
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [filterUi, setFilterUi] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [focusFilterId, setFocusFilterId] = useState(null);
  const [matchingFilingsCount, setMatchingFilingsCount] = useState(null);
  const [matchingFilingsLoading, setMatchingFilingsLoading] = useState(false);
  const [draftMatchingFilingsCount, setDraftMatchingFilingsCount] = useState(null);
  const [draftMatchingFilingsLoading, setDraftMatchingFilingsLoading] = useState(false);
  const draftMatchSeq = useRef(0);
  const [options, setOptions] = useState({
    jurisdictions: [],
    states: [],
    companies: [],
    filing_types: [],
    toi: [],
    sub_toi: [],
    state_toi: [],
    state_sub_toi: [],
    statuses: [],
    product_names: [],
  });
  const matchSeq = useRef(0);

  const isFilterEnabled = useCallback(
    (dim) => filterOptionsLoaded && filterUi[dim] !== false,
    [filterOptionsLoaded, filterUi],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getFilterOptions();
        if (cancelled) return;
        setOptions({
          jurisdictions: mapJurisdictionOptions(data.jurisdictions),
          states: mapStateOptions(data.states),
          companies: data.companies || [],
          filing_types: data.filing_types || [],
          toi: data.toi || [],
          sub_toi: data.sub_toi || [],
          state_toi: data.state_toi || [],
          state_sub_toi: data.state_sub_toi || [],
          statuses: data.statuses || [],
          product_names: data.product_names || [],
        });
        const nextUi = {};
        FILTER_DIMENSION_KEYS.forEach((k) => { nextUi[k] = data.enabled_filters?.[k] !== false; });
        setFilterUi(nextUi);
        setAppliedParams((prev) => pruneParamsForEnabled(prev, nextUi));
        setDraftParams((prev) => pruneParamsForEnabled(prev, nextUi));
        if (!isDimensionEnabled(nextUi, 'submitted_dates')) {
          setAppliedDateStart('');
          setAppliedDateEnd('');
          setDraftDateStart('');
          setDraftDateEnd('');
        }
        if (!isDimensionEnabled(nextUi, 'disposition_dates')) {
          setAppliedDispositionDateStart('');
          setAppliedDispositionDateEnd('');
          setDraftDispositionDateStart('');
          setDraftDispositionDateEnd('');
        }
        setFilterOptionsLoaded(true);
      } catch {
        if (cancelled) return;
        clearFilterOptionsCache();
        setOptions((prev) => ({
          ...prev,
          jurisdictions: mapJurisdictionOptions(Object.values(JURISDICTION_MAP)),
          states: mapStateOptions(
            [...new Set(Object.values(JURISDICTION_MAP).filter((code) => code !== 'Compact'))].sort(),
          ),
        }));
        setFilterUi(Object.fromEntries(FILTER_DIMENSION_KEYS.map((k) => [k, true])));
        setFilterOptionsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeFiltersCount = useMemo(
    () => countActiveFilters(
      appliedParams,
      appliedDateStart,
      appliedDateEnd,
      appliedDispositionDateStart,
      appliedDispositionDateEnd,
      isFilterEnabled,
    ),
    [isFilterEnabled, appliedParams, appliedDateStart, appliedDateEnd, appliedDispositionDateStart, appliedDispositionDateEnd],
  );

  const draftFiltersCount = useMemo(
    () => countActiveFilters(
      draftParams,
      draftDateStart,
      draftDateEnd,
      draftDispositionDateStart,
      draftDispositionDateEnd,
      isFilterEnabled,
    ),
    [isFilterEnabled, draftParams, draftDateStart, draftDateEnd, draftDispositionDateStart, draftDispositionDateEnd],
  );

  const activeScopeChips = useMemo(
    () => buildScopeChips(
      appliedParams,
      appliedDateStart,
      appliedDateEnd,
      appliedDispositionDateStart,
      appliedDispositionDateEnd,
      isFilterEnabled,
    ),
    [isFilterEnabled, appliedParams, appliedDateStart, appliedDateEnd, appliedDispositionDateStart, appliedDispositionDateEnd],
  );

  const buildFilterPayload = useCallback(
    () => buildPayloadFrom(
      appliedParams,
      appliedDateStart,
      appliedDateEnd,
      appliedDispositionDateStart,
      appliedDispositionDateEnd,
      isFilterEnabled,
    ),
    [isFilterEnabled, appliedParams, appliedDateStart, appliedDateEnd, appliedDispositionDateStart, appliedDispositionDateEnd],
  );

  const buildDraftFilterPayload = useCallback(
    () => buildPayloadFrom(
      draftParams,
      draftDateStart,
      draftDateEnd,
      draftDispositionDateStart,
      draftDispositionDateEnd,
      isFilterEnabled,
    ),
    [isFilterEnabled, draftParams, draftDateStart, draftDateEnd, draftDispositionDateStart, draftDispositionDateEnd],
  );

  useEffect(() => {
    if (!filtersOpen) return;
    setDraftParams(cloneParams(appliedParams));
    setDraftDateStart(appliedDateStart);
    setDraftDateEnd(appliedDateEnd);
    setDraftDispositionDateStart(appliedDispositionDateStart);
    setDraftDispositionDateEnd(appliedDispositionDateEnd);
  }, [filtersOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- sync draft when drawer opens

  useEffect(() => {
    if (!activeFiltersCount) {
      matchSeq.current += 1;
      setMatchingFilingsCount(null);
      setMatchingFilingsLoading(false);
      return undefined;
    }
    const seq = ++matchSeq.current;
    setMatchingFilingsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await getFilterMatchCount(buildFilterPayload());
        if (seq === matchSeq.current) setMatchingFilingsCount(data.matching_filings_count);
      } catch {
        if (seq === matchSeq.current) setMatchingFilingsCount(null);
      } finally {
        if (seq === matchSeq.current) setMatchingFilingsLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [activeFiltersCount, buildFilterPayload]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    if (!draftFiltersCount) {
      draftMatchSeq.current += 1;
      setDraftMatchingFilingsCount(null);
      setDraftMatchingFilingsLoading(false);
      return undefined;
    }
    const seq = ++draftMatchSeq.current;
    setDraftMatchingFilingsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await getFilterMatchCount(buildDraftFilterPayload());
        if (seq === draftMatchSeq.current) setDraftMatchingFilingsCount(data.matching_filings_count);
      } catch {
        if (seq === draftMatchSeq.current) setDraftMatchingFilingsCount(null);
      } finally {
        if (seq === draftMatchSeq.current) setDraftMatchingFilingsLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [filtersOpen, draftFiltersCount, buildDraftFilterPayload]);

  const clearDraftFilters = () => {
    draftMatchSeq.current += 1;
    setDraftParams(defaultParams);
    setDraftDateStart('');
    setDraftDateEnd('');
    setDraftDispositionDateStart('');
    setDraftDispositionDateEnd('');
    setDraftMatchingFilingsCount(null);
    setDraftMatchingFilingsLoading(false);
  };

  const clearFilters = () => {
    matchSeq.current += 1;
    draftMatchSeq.current += 1;
    setAppliedParams(defaultParams);
    setAppliedDateStart('');
    setAppliedDateEnd('');
    setAppliedDispositionDateStart('');
    setAppliedDispositionDateEnd('');
    setDraftParams(defaultParams);
    setDraftDateStart('');
    setDraftDateEnd('');
    setDraftDispositionDateStart('');
    setDraftDispositionDateEnd('');
    setMatchingFilingsCount(null);
    setMatchingFilingsLoading(false);
    setDraftMatchingFilingsCount(null);
    setDraftMatchingFilingsLoading(false);
  };

  const applyFilters = () => {
    setAppliedParams(cloneParams(draftParams));
    setAppliedDateStart(draftDateStart);
    setAppliedDateEnd(draftDateEnd);
    setAppliedDispositionDateStart(draftDispositionDateStart);
    setAppliedDispositionDateEnd(draftDispositionDateEnd);
    setFiltersOpen(false);
  };

  const onCloseFilters = () => {
    setFiltersOpen(false);
  };

  useEffect(() => {
    if (!filtersOpen || !focusFilterId) return undefined;
    const timer = setTimeout(() => {
      document.getElementById(focusFilterId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFocusFilterId(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [filtersOpen, focusFilterId]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.accordion-item') && !e.target.closest('.react-datepicker')) {
        setOpenFilter(null);
      }
    };
    if (filtersOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [filtersOpen]);

  const openFilterSection = (sectionKey) => {
    if (typeof sectionKey === 'string' && FILTER_SECTION_BY_KEY[sectionKey]) {
      const section = FILTER_SECTION_BY_KEY[sectionKey];
      const dimension = FILTER_SECTION_TO_DIMENSION[section];
      if (!dimension || isFilterEnabled(dimension)) {
        setOpenFilter(section);
        setFocusFilterId(FILTER_DOM_IDS[section]);
      }
    }
    setFiltersOpen(true);
  };

  const openFiltersFromLanding = openFilterSection;
  const openFiltersSection = openFilterSection;

  const toggleFilter = (section) => {
    setOpenFilter((prev) => (prev === section ? null : section));
  };

  const filtersMatchText = matchingFilingsLoading
    ? 'Checking matches...'
    : matchingFilingsCount !== null
      ? `${matchingFilingsCount} filing${matchingFilingsCount === 1 ? '' : 's'} match`
      : 'Refine the answer scope before asking';

  const drawerFiltersMatchText = draftMatchingFilingsLoading
    ? 'Checking matches...'
    : draftMatchingFilingsCount !== null
      ? `${draftMatchingFilingsCount} filing${draftMatchingFilingsCount === 1 ? '' : 's'} match`
      : 'Refine the answer scope before asking';

  const filterDrawer = () => (
    <>
      {isFilterEnabled('filing_types') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.filingTypes}
          title="Filing Type"
          Icon={FileText}
          options={options.filing_types}
          value={draftParams.filingTypes}
          onChange={(v) => setDraftParams((p) => ({ ...p, filingTypes: v }))}
          searchPlaceholder="Search filing types"
          isOpen={openFilter === 'filingTypes'}
          onToggle={() => toggleFilter('filingTypes')}
        />
      )}
      {isFilterEnabled('statuses') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.statuses}
          title="SERFF Status"
          Icon={ShieldCheck}
          options={options.statuses}
          value={draftParams.statuses}
          onChange={(v) => setDraftParams((p) => ({ ...p, statuses: v }))}
          searchPlaceholder="Search statuses"
          isOpen={openFilter === 'statuses'}
          onToggle={() => toggleFilter('statuses')}
        />
      )}
      {isFilterEnabled('has_objections') && (
        <AnswersRagObjectionsFilter
          domId={FILTER_DOM_IDS.hasObjections}
          value={draftParams.hasObjections}
          onChange={(v) => setDraftParams((p) => ({ ...p, hasObjections: v }))}
          isOpen={openFilter === 'hasObjections'}
          onToggle={() => toggleFilter('hasObjections')}
        />
      )}
      {isFilterEnabled('jurisdictions') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.jurisdictions}
          title="Jurisdiction"
          Icon={Globe}
          options={options.jurisdictions}
          value={draftParams.jurisdictions}
          optionLabel={(x) => x.label || x}
          optionValue={(x) => x.value || x}
          onChange={(v) => setDraftParams((p) => ({ ...p, jurisdictions: v }))}
          searchPlaceholder="Search jurisdictions"
          isOpen={openFilter === 'jurisdictions'}
          onToggle={() => toggleFilter('jurisdictions')}
        />
      )}
      {isFilterEnabled('states') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.states}
          title="State"
          Icon={MapPin}
          options={options.states}
          value={draftParams.states}
          optionLabel={(x) => x.label || labelForStateCode(x)}
          optionValue={(x) => x.value || x}
          onChange={(v) => setDraftParams((p) => ({ ...p, states: v }))}
          searchPlaceholder="Search states"
          isOpen={openFilter === 'states'}
          onToggle={() => toggleFilter('states')}
        />
      )}
      {isFilterEnabled('companies') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.companies}
          title="Company"
          Icon={Building}
          options={options.companies}
          value={draftParams.companies}
          onChange={(v) => setDraftParams((p) => ({ ...p, companies: v }))}
          searchPlaceholder="Search companies"
          isOpen={openFilter === 'companies'}
          onToggle={() => toggleFilter('companies')}
        />
      )}
      {isFilterEnabled('product_names') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.productNames}
          title="Product name"
          Icon={Package}
          options={options.product_names}
          value={draftParams.productNames}
          onChange={(v) => setDraftParams((p) => ({ ...p, productNames: v }))}
          searchPlaceholder="Search products"
          isOpen={openFilter === 'productNames'}
          onToggle={() => toggleFilter('productNames')}
        />
      )}
      {isFilterEnabled('toi') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.toi}
          title="Type of Insurance (TOI)"
          Icon={Folder}
          options={options.toi}
          value={draftParams.toi}
          onChange={(v) => setDraftParams((p) => ({ ...p, toi: v }))}
          searchPlaceholder="Search TOI"
          isOpen={openFilter === 'toi'}
          onToggle={() => toggleFilter('toi')}
        />
      )}
      {isFilterEnabled('sub_toi') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.subToi}
          title="Sub-TOI"
          Icon={Folder}
          options={options.sub_toi}
          value={draftParams.subToi}
          onChange={(v) => setDraftParams((p) => ({ ...p, subToi: v }))}
          searchPlaceholder="Search Sub-TOI"
          isOpen={openFilter === 'subToi'}
          onToggle={() => toggleFilter('subToi')}
        />
      )}
      {isFilterEnabled('state_toi') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.stateToi}
          title="State TOI"
          Icon={Folder}
          options={options.state_toi}
          value={draftParams.stateToi}
          onChange={(v) => setDraftParams((p) => ({ ...p, stateToi: v }))}
          searchPlaceholder="Search state TOI"
          isOpen={openFilter === 'stateToi'}
          onToggle={() => toggleFilter('stateToi')}
        />
      )}
      {isFilterEnabled('state_sub_toi') && (
        <AnswersRagAccordionFilter
          domId={FILTER_DOM_IDS.stateSubToi}
          title="State sub-TOI"
          Icon={Folder}
          options={options.state_sub_toi}
          value={draftParams.stateSubToi}
          onChange={(v) => setDraftParams((p) => ({ ...p, stateSubToi: v }))}
          searchPlaceholder="Search state sub-TOI"
          isOpen={openFilter === 'stateSubToi'}
          onToggle={() => toggleFilter('stateSubToi')}
        />
      )}
      {isFilterEnabled('submitted_dates') && (
        <AnswersRagDateRangeFilter
          domId={FILTER_DOM_IDS.submittedDates}
          title="Submission dates"
          startDate={draftDateStart}
          endDate={draftDateEnd}
          onChange={({ start, end }) => {
            setDraftDateStart(start);
            setDraftDateEnd(end);
          }}
        />
      )}
      {isFilterEnabled('disposition_dates') && (
        <AnswersRagDateRangeFilter
          domId={FILTER_DOM_IDS.dispositionDates}
          title="Disposition dates"
          startDate={draftDispositionDateStart}
          endDate={draftDispositionDateEnd}
          onChange={({ start, end }) => {
            setDraftDispositionDateStart(start);
            setDraftDispositionDateEnd(end);
          }}
        />
      )}
    </>
  );

  return {
    activeFiltersCount,
    activeScopeChips,
    buildFilterPayload,
    applyFilters,
    clearDraftFilters,
    clearFilters,
    filterDrawer,
    filterOptionsLoaded,
    drawerFiltersMatchText,
    filtersMatchText,
    filtersOpen,
    openFiltersFromLanding,
    openFiltersSection,
    setFiltersOpen,
    onCloseFilters,
  };
}
